import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { Loading } from "@/components/Loading";

const SCOPES = [{ v: "first", l: "First order" }, { v: "all", l: "All orders" }];

const ScopeSelect = ({ value, onChange, testid }) => (
  <select className="field !min-h-0 !py-2 !text-base" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid}>
    {SCOPES.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
  </select>
);

const AddForm = ({ onAdded }) => {
  const [email, setEmail] = useState("");
  const [note, setNote] = useState("");
  const [scope, setScope] = useState("first");
  const [busy, setBusy] = useState(false);
  const add = async (e) => {
    e.preventDefault();
    setBusy(true);
    try { await api.post("/admin/free-passes", { email: email.trim(), note, scope }); toast.success("Added to the free order list"); setEmail(""); setNote(""); setScope("first"); onAdded(); }
    catch (err) { toast.error(errMsg(err)); }
    finally { setBusy(false); }
  };
  return (
    <form onSubmit={add} className="paper-card grid gap-3 p-5 md:grid-cols-12 md:items-end" data-testid="free-pass-form">
      <div className="md:col-span-4"><label className="label">Email address</label><input type="email" required className="field !min-h-0 !py-2 !text-base" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="grandma@example.com" data-testid="free-pass-email-input" /></div>
      <div className="md:col-span-3"><label className="label">Free for</label><ScopeSelect value={scope} onChange={setScope} testid="free-pass-scope-select" /></div>
      <div className="md:col-span-3"><label className="label">Note (optional)</label><input className="field !min-h-0 !py-2 !text-base" value={note} maxLength={200} onChange={(e) => setNote(e.target.value)} placeholder="e.g. Influencer, family" data-testid="free-pass-note-input" /></div>
      <button className="btn-primary !min-h-0 !py-2 !text-base md:col-span-2" disabled={busy} data-testid="free-pass-add-button">Add</button>
    </form>
  );
};

const PassRow = ({ p, reload }) => {
  const act = async (fn, msg) => { try { await fn(); toast.success(msg); reload(); } catch (e) { toast.error(errMsg(e)); } };
  const enc = encodeURIComponent(p.email);
  const all = p.scope === "all";
  const uses = p.uses ?? (p.used_order_id ? 1 : 0);
  return (
    <tr className="border-t border-[#E6DEC6]" data-testid={`free-pass-row-${p.email}`}>
      <td className="px-3 py-3">{p.email}</td>
      <td className="min-w-[10rem] px-3 py-3"><ScopeSelect value={p.scope || "first"} onChange={(v) => act(() => api.patch(`/admin/free-passes/${enc}`, { scope: v }), v === "all" ? "Every order is now free" : "Only one free order now")} testid={`free-pass-scope-${p.email}`} /></td>
      <td className="px-3 py-3">{p.note || "—"}</td>
      <td className="px-3 py-3" data-testid={`free-pass-status-${p.email}`}>
        {all ? <span><b className="text-[#2E6930]">Always free</b>{uses > 0 && <> · used {uses} {uses === 1 ? "time" : "times"}</>}</span>
          : p.used_order_id ? <span>Used on <b>{p.used_order_id}</b></span> : <b className="text-[#2E6930]">Available</b>}
      </td>
      <td className="flex flex-wrap gap-2 px-3 py-3">
        {!all && p.used_order_id && <button className="btn-outline !min-h-0 !py-1" onClick={() => act(() => api.post(`/admin/free-passes/${enc}/reset`), "They get one more free order")} data-testid={`free-pass-reset-${p.email}`}>Give another</button>}
        <button className="btn-secondary !min-h-0 !py-1" onClick={() => window.confirm(`Remove ${p.email} from the list?`) && act(() => api.delete(`/admin/free-passes/${enc}`), "Removed")} data-testid={`free-pass-remove-${p.email}`}>Remove</button>
      </td>
    </tr>
  );
};

export const AdminFreeOrders = () => {
  const [rows, setRows] = useState(null);
  const load = useCallback(() => api.get("/admin/free-passes").then(({ data }) => setRows(data)), []);
  useEffect(() => { load(); }, [load]);
  if (!rows) return <Loading />;
  return (
    <div className="space-y-5" data-testid="admin-free-orders">
      <p className="text-lg text-[#5C4A3E]">People on this list skip payment and their keepsakes are printed and shipped at your cost. Choose <b>First order</b> (one free order) or <b>All orders</b> (every order free until you change it or remove them). They must sign in with this same email.</p>
      <AddForm onAdded={load} />
      <div className="overflow-x-auto paper-card">
        <table className="w-full text-left text-base">
          <thead className="bg-[#F2EBE1]"><tr>{["Email", "Free for", "Note", "Status", ""].map((h) => <th key={h} className="px-3 py-3">{h}</th>)}</tr></thead>
          <tbody>
            {rows.map((p) => <PassRow key={p.email} p={p} reload={load} />)}
            {!rows.length && <tr><td colSpan={5} className="px-3 py-6 text-center text-[#8A7A6E]" data-testid="free-pass-empty">No one on the list yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
};
